import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
  CircularProgress, Alert, Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Lightbulb as SuggestIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const DomainExclusions = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission('email_extractor_admin');

  // State
  const [userDomains, setUserDomains] = useState([]);
  const [globalDomains, setGlobalDomains] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [addGlobalDialog, setAddGlobalDialog] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [newReason, setNewReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [userRes, globalRes, suggestRes] = await Promise.all([
        api.get('/email-extractor/domains/excluded'),
        api.get('/email-extractor/domains/global'),
        api.get('/email-extractor/domains/suggestions')
      ]);
      setUserDomains(userRes.data.data);
      setGlobalDomains(globalRes.data.data);
      setSuggestions(suggestRes.data.data);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers
  const handleAddUserDomain = async () => {
    if (!newDomain.trim()) return;

    try {
      setProcessing(true);
      await api.post('/email-extractor/domains/excluded', {
        domain: newDomain.trim(),
        reason: newReason.trim() || 'Manual exclusion'
      });
      setAddDialog(false);
      setNewDomain('');
      setNewReason('');
      fetchData();
    } catch (error) {
      console.error('Failed to add domain:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleAddGlobalDomain = async () => {
    if (!newDomain.trim()) return;

    try {
      setProcessing(true);
      await api.post('/email-extractor/domains/global', {
        domain: newDomain.trim(),
        reason: newReason.trim() || 'Global exclusion'
      });
      setAddGlobalDialog(false);
      setNewDomain('');
      setNewReason('');
      fetchData();
    } catch (error) {
      console.error('Failed to add global domain:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveUserDomain = async (id) => {
    try {
      await api.delete(`/email-extractor/domains/excluded/${id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to remove domain:', error);
    }
  };

  const handleRemoveGlobalDomain = async (id) => {
    try {
      await api.delete(`/email-extractor/domains/global/${id}`);
      fetchData();
    } catch (error) {
      console.error('Failed to remove global domain:', error);
    }
  };

  const handleAddSuggestion = async (suggestion) => {
    try {
      await api.post('/email-extractor/domains/excluded', {
        domain: suggestion.domain,
        reason: suggestion.reason
      });
      fetchData();
    } catch (error) {
      console.error('Failed to add suggestion:', error);
    }
  };

  const handleAddAllSuggestions = async () => {
    try {
      setProcessing(true);
      await api.post('/email-extractor/domains/excluded/bulk', {
        domains: suggestions
      });
      fetchData();
    } catch (error) {
      console.error('Failed to add all suggestions:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/email-extractor')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Domain Exclusions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialog(true)}
        >
          Add Exclusion
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Suggestions */}
        {suggestions.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SuggestIcon color="warning" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Suggested Exclusions
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={handleAddAllSuggestions}
                    disabled={processing}
                  >
                    Add All
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {suggestions.map((s) => (
                    <Chip
                      key={s.domain}
                      label={s.domain}
                      onClick={() => handleAddSuggestion(s)}
                      onDelete={() => handleAddSuggestion(s)}
                      deleteIcon={<AddIcon />}
                      variant="outlined"
                      color="warning"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* User Exclusions */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Your Exclusions ({userDomains.length})
              </Typography>
              {userDomains.length === 0 ? (
                <Alert severity="info">
                  You haven't excluded any domains yet. Add domains you want to skip during extraction.
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Domain</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {userDomains.map((domain) => (
                        <TableRow key={domain.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {domain.domain}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {domain.reason || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveUserDomain(domain.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Global Exclusions */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Global Exclusions ({globalDomains.length})
                </Typography>
                {isAdmin && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setAddGlobalDialog(true)}
                  >
                    Add Global
                  </Button>
                )}
              </Box>
              {globalDomains.length === 0 ? (
                <Alert severity="info">
                  No global exclusions set. These apply to all users.
                </Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Domain</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Added By</TableCell>
                        {isAdmin && <TableCell align="center">Action</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {globalDomains.map((domain) => (
                        <TableRow key={domain.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {domain.domain}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {domain.reason || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {domain.creator
                                ? `${domain.creator.first_name} ${domain.creator.last_name}`
                                : 'System'}
                            </Typography>
                          </TableCell>
                          {isAdmin && (
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveGlobalDomain(domain.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add User Domain Dialog */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Domain Exclusion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Emails from excluded domains will be skipped during extraction.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Domain"
            placeholder="e.g., gmail.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Reason (optional)"
            placeholder="Why exclude this domain?"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddUserDomain}
            disabled={!newDomain.trim() || processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Global Domain Dialog */}
      <Dialog open={addGlobalDialog} onClose={() => setAddGlobalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Global Exclusion</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This exclusion will apply to ALL users in the system.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Domain"
            placeholder="e.g., gmail.com"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Reason (optional)"
            placeholder="Why exclude this domain globally?"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddGlobalDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleAddGlobalDomain}
            disabled={!newDomain.trim() || processing}
          >
            {processing ? <CircularProgress size={20} /> : 'Add Global'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DomainExclusions;
